import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { demoMode } from '@/lib/demoMode';
import { FIXTURES } from '@/lib/fixtures';

// Use current origin so the same bundle works on preview + custom prod domains (e.g. meettokeep.com).
// Both preview and native Emergent deployment proxy /api/* to the backend via ingress.
const BACKEND_URL = typeof window !== 'undefined' ? window.location.origin : process.env.REACT_APP_BACKEND_URL;
export { BACKEND_URL };

const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true
});

api.interceptors.request.use(config => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Feb 2026 · Client-Side Demo Preview — short-circuit specific GETs to
  // static fixtures so mobile can regression-test Matches / Likes / Messages
  // / Views UI without touching the backend. Ships behind a Settings toggle;
  // localStorage flag `mtk_demo_mode_v1`. Fixtures live in /lib/fixtures.js.
  if (demoMode.isEnabled() && (config.method || 'get').toLowerCase() === 'get') {
    const stub = _demoStubFor(config.url || '');
    if (stub !== undefined) {
      // Signal to the response interceptor to bypass the network entirely.
      config.adapter = () => Promise.resolve({
        data: stub, status: 200, statusText: 'OK (demo)',
        headers: {}, config, request: { _demo: true },
      });
    }
  }
  return config;
});

// Route → fixture mapping. Only 4 read-only endpoints today; mutations still
// hit the live backend so nothing breaks silently. `undefined` = passthrough.
function _demoStubFor(url) {
  // Strip any leading /api since axios baseURL already includes it.
  const path = url.replace(/^\/?api\//, '/').split('?')[0];
  if (path === '/matches' || path === 'matches') return FIXTURES.matches;
  if (path === '/likes-received' || path === 'likes-received') return FIXTURES.likesReceived;
  if (path === '/profile-views' || path === 'profile-views') return FIXTURES.profileViews;
  const chatMatch = path.match(/^\/?chat\/([^/]+)$/);
  if (chatMatch) {
    const stub = FIXTURES.chatFor(chatMatch[1]);
    if (stub) return stub;
  }
  return undefined;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();

    // Register service worker for push notifications
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {});
    }
  }, [checkAuth]);

  // Presence heartbeat — ping every 60s while authenticated + tab visible
  useEffect(() => {
    if (!user) return;
    const ping = () => { if (!document.hidden) api.post('/presence/ping').catch(() => {}); };
    ping();
    const interval = setInterval(ping, 60000);
    const onVis = () => { if (!document.hidden) ping(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', onVis); };
  }, [user]);

  const login = (userData, sessionToken) => {
    if (sessionToken) localStorage.setItem('session_token', sessionToken);
    setUser(userData);
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch { /* ignore */ }
    localStorage.removeItem('session_token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}
